import React from "react";

import { KnxChapterItem } from "./KnxChapterItem";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import Device from "../../models/Device";
import { isUiModeDeveloper } from "../../uiMode";
import { knxChapter_Keydown } from "./KeyboardHandler";
import { noItx } from "../../translationItx/itx";

function focusFirstChapterItemWithContent(chapterItem: KnxChapterItem): KnxChapterItem {
  if (chapterItem.content) {
    chapterItem.expanded = true;
    setTimeout(() => {
      chapterItem.ref?.current?.focus();
    }, 100);
    return chapterItem;
  }

  if (chapterItem.subChapters) {
    for (const subChapter of chapterItem.subChapters) {
      const x = focusFirstChapterItemWithContent(subChapter);
      if (x) {
        x.expanded = true;
        return x;
      }
    }
  }

  chapterItem.expanded = true;
  chapterItem.ref?.current?.focus();
  return chapterItem;
}

function ChapterHeader(props: {
  device: Device;
  knxChapterItem: KnxChapterItem;

  id: string;
  hidden: boolean;
  productionHeader?: string;
  debugHeader?: any;
  onClick: (knxChapterItem: KnxChapterItem) => void;
}) {
  const { device, id, knxChapterItem, hidden, debugHeader, onClick } = props;
  let { productionHeader } = props;
  useOnChangeWatcher(device.currentKnxChapter);
  const selected = device.currentKnxChapter.value == knxChapterItem;

  productionHeader = device.substitutePropertyPlaceholders(productionHeader || "", knxChapterItem.instanceId);

  console.log(
    `render chapter header id: ${knxChapterItem.chapterId}, header: ${productionHeader}, selected: ${selected}`,
  );

  if (knxChapterItem.access == "None" && !isUiModeDeveloper()) {
    return null;
  }

  const icon = knxChapterItem.icon;
  let iconElement: any = undefined;
  if (icon) {
    const filename = icon + ".png";
    const iconFileSrc = device.template?.knxProductData?.iconFileSrc[filename];
    console.log("iconFileSrc", filename, iconFileSrc);
    iconElement = <img className="knxIcon" src={iconFileSrc} alt="icon" />;
  }

  return (
    <div
      key="header"
      className={`knxChapterHeader ${selected ? "selected" : ""}`}
      style={{ backgroundColor: knxChapterItem.access == "None" ? "red" : undefined }}
      onClick={() => {
        const focusedChapter = focusFirstChapterItemWithContent(knxChapterItem);

        if (onClick) {
          onClick(focusedChapter);
        }
      }}
    >
      {iconElement}
      {productionHeader}
      {isUiModeDeveloper() && debugHeader && (
        <div style={{ color: "grey", marginLeft: "20px", fontSize: "0.7em" }}>{debugHeader}</div>
      )}
      {isUiModeDeveloper() && (
        <div style={{ color: "grey", marginLeft: "20px", fontSize: "0.7em" }}>
          {noItx("id:")} {knxChapterItem.chapterId}
          {noItx(", instanceId:")} {knxChapterItem.instanceId}
          {knxChapterItem.indent !== undefined && (
            <span>
              {noItx(", indent:")} {knxChapterItem.indent}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function KnxChapter(props: {
  device: Device;
  knxChapterItem: KnxChapterItem;
  onClick: (knxChapterItem: KnxChapterItem) => void;
}) {
  const { device, knxChapterItem, onClick } = props;

  const knxChapterRef = React.createRef<HTMLDivElement>();

  useOnChangeWatcher(device.currentKnxChapter);
  useOnChangeWatcher(knxChapterItem);

  if (!knxChapterItem.content && (!knxChapterItem.subChapters || knxChapterItem.subChapters.length == 0)) {
    return null;
  }

  knxChapterItem.ref = knxChapterRef;

  const subChaptersLength = knxChapterItem.subChapters.filter((c) => c.content || c.subChapters?.length > 0).length;

  return (
    <div
      tabIndex={1}
      className={"knxChapter estadaColumn left top " + (knxChapterItem.expanded ? "IsExpanded" : "")}
      ref={knxChapterRef}
      onKeyDown={(e) => {
        if (knxChapterRef.current) {
          knxChapter_Keydown(device, knxChapterRef.current, e, subChaptersLength > 0);
        }
      }}
      onFocus={(ev) => {
        if (ev.target !== ev.currentTarget) {
          return;
        }
        ev.stopPropagation();
        console.log("focus chapter", knxChapterItem.chapterId);
        device.currentKnxChapter.value = knxChapterItem;
        device.currentKnxChapterId = knxChapterItem.chapterId;
      }}
    >
      <div className="estadaRow left top">
        <div
          className="toggleIcon"
          onClick={(e) => {
            e.stopPropagation();
            knxChapterItem.expanded = !knxChapterItem.expanded;
          }}
        >
          <div className={`knxCaret las ${subChaptersLength == 0 ? "hasNoChildren" : ""}`} />
        </div>

        <div>
          {knxChapterItem.headerData && (
            <ChapterHeader
              device={device}
              knxChapterItem={knxChapterItem}
              {...knxChapterItem.headerData}
              onClick={onClick}
            />
          )}
        </div>
      </div>

      {knxChapterItem.subChapters && subChaptersLength > 0 && (
        <div className="knxChapterChildren" style={{ paddingLeft: "20px" }}>
          {knxChapterItem.subChapters.map((subChapter) => (
            <KnxChapter key={subChapter.id} device={device} knxChapterItem={subChapter} onClick={onClick} />
          ))}
        </div>
      )}
    </div>
  );
}
